import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSettings, digitsOnly } from "@/contexts/SettingsContext";

export default function Contact() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", email: "", mobile: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact-messages", form);
      toast.success("Message sent. We'll respond within 24 hours.");
      setForm({ name: "", email: "", mobile: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const items = [
    { i: MapPin, t: "Address", d: s.address },
    { i: Phone, t: "General", d: s.phone, href: `tel:${digitsOnly(s.phone)}` },
    { i: Phone, t: "Emergency (24x7)", d: s.emergency_phone, href: `tel:${digitsOnly(s.emergency_phone)}`, em: true },
    { i: MessageCircle, t: "WhatsApp", d: s.whatsapp, href: `https://wa.me/${digitsOnly(s.whatsapp)}` },
    { i: Mail, t: "Email", d: s.email, href: `mailto:${s.email}` },
    { i: Clock, t: "Hours", d: "OPD: 8 AM – 9 PM • Emergency: 24x7" },
  ];

  return (
    <div data-testid="contact-page">
      <section className="hero-bg py-12">
        <div className="container-x">
          <span className="font-cursive text-2xl text-brand-purple">Reach Out</span>
          <h1 className="text-4xl sm:text-5xl font-bold">Contact Us</h1>
        </div>
      </section>

      <section className="container-x py-10 grid md:grid-cols-2 gap-10">
        <div>
          <div className="space-y-5">
            {items.map((x, i) => (
              <div key={i} className="flex gap-4 items-start" data-testid={`contact-info-${x.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${x.em ? "bg-red-100 text-red-600" : "bg-brand-purple/10 text-brand-purple"}`}><x.i className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">{x.t}</div>
                  {x.href ? <a href={x.href} className="font-semibold hover:text-brand-purple">{x.d}</a> : <div className="font-semibold">{x.d}</div>}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 aspect-video">
            {s.map_embed ? (
              <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: s.map_embed }} />
            ) : (
              <iframe title="Hospital location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.7252!2d78.4866!3d17.385!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99c0e5e5e5e5%3A0x0!2sHyderabad!5e0!3m2!1sen!2sin!4v0" width="100%" height="100%" loading="lazy" style={{ border: 0 }} />
            )}
          </div>
        </div>

        <form onSubmit={submit} className="card-clean p-6 md:p-8 self-start" data-testid="contact-form">
          <h2 className="text-2xl font-bold">Send us a message</h2>
          <p className="text-sm text-slate-500 mt-1">We'll respond within 24 hours.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="contact-name" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="contact-email" />
            <input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile" data-testid="contact-mobile" />
            <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" data-testid="contact-subject" />
            <textarea required rows="5" className="sm:col-span-2" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message" data-testid="contact-message" />
            <button disabled={sending} className="btn-primary sm:col-span-2 justify-center" data-testid="contact-submit">{sending ? "Sending…" : "Send Message"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
